/**
 * Send state of an outbound bubble, from the server (`ChatMessage.send`, outbox) plus the UI's own flags
 * while a request is in flight. docs/outbound.md
 */
import type { ChatMessage, SendMessageBody, ThreadItem } from '../api/types'

export type SendStatus = 'pending' | 'sent' | 'failed'

/** Client-only fields on a thread item. */
export interface UiSendFlags {
  /** A request for this bubble is in flight (first send, or "ลองใหม่"). */
  pending?: boolean
  /** The request failed before the server confirmed anything (network / timeout / 5xx). */
  failed?: boolean
  /** Why, when the server never gave us a `send.error`. */
  localError?: string | null
  /** The API refused this local bubble (4xx): nothing was stored, so it may be hidden. */
  refused?: boolean
  /** Body of an unconfirmed send, re-posted as-is (same client_id) by "ลองใหม่". */
  sendBody?: SendMessageBody
}

export type UiThreadItem = ThreadItem & UiSendFlags

const TMP = 'tmp-'

export const newClientId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

export const tempId = (clientId: string): string => `${TMP}${clientId}`

/** client_id of the n-th library picture (1-based) of one send — same rule as api/app/outbound.py. */
export const mediaClientId = (clientId: string, n: number, hasText: boolean): string =>
  n === 1 && !hasText ? clientId : `${clientId}:m${n}`

/** Every client_id one send creates: the text (if any), then each picture. */
export function sendClientIds(body: SendMessageBody): string[] {
  const hasText = body.text.trim() !== ''
  const pics = (body.media_ids ?? []).map((_, i) => mediaClientId(body.client_id, i + 1, hasText))
  return hasText ? [body.client_id, ...pics] : pics.length ? pics : [body.client_id]
}

/** Only the UI has this bubble: the server never confirmed it (id is still the temporary one). */
export const isLocalOnly = (item: { id: string }): boolean => item.id.startsWith(TMP)

/** null for inbound messages and system lines. Old outbound rows without `send` are sent. */
export function sendStatusOf(item: UiThreadItem): SendStatus | null {
  if (item.kind !== 'message' || item.direction !== 'out') return null
  if (item.pending) return 'pending'
  if (item.failed) return 'failed'
  return item.send?.status ?? 'sent'
}

export function sendErrorOf(item: UiThreadItem): string | null {
  if (item.kind !== 'message') return null
  return item.localError ?? item.send?.error ?? null
}

/** The temporary bubble a realtime/POST result stands for (same client_id), if any. */
export function findTemp(list: UiThreadItem[], msg: ChatMessage): number {
  const cid = msg.send?.client_id
  if (!cid) return -1
  return list.findIndex((x) => x.kind === 'message' && isLocalOnly(x) && x.send?.client_id === cid)
}
